/**
 * GOLDZENN Gold Price Feed v3 DRAFT (2026-08-06) — PATCHED COPY, NOT DEPLOYED
 *
 * v3 adds the runtime half of the mak-gold-derived mechanism so that what a
 * browser paints and what update_gold_prices.py bakes into the article HTML are
 * the same string. The source-HTML side matters more (crawlers and AI engines
 * read the raw body, and the feed loader is not even included on every article
 * template), but the two must never disagree.
 *
 *   .mak-gold-derived[data-karat][data-unit][data-mult][data-fmt]
 *        textContent = karats[karat][unit] * mult
 *        data-karat  10 | 14 | 18 | 22 | 24 | pure     (required)
 *        data-unit   gram | dwt | oz | tola | kg       (default gram)
 *        data-mult   any float                         (default 1)
 *        data-fmt    int | currency                    (default currency)
 *
 * "pure", "tola" and "kg" come from enrich() in the updater, which now runs on
 * BOTH feed paths, so they are present in window.GZ_GOLD_DATA and in
 * gold-prices-live.json. Older cached payloads simply lack the key and the span
 * is left alone — the static fallback in the HTML stays visible.
 *
 * Data source priority:
 *   1. window.GZ_GOLD_DATA  — inlined server-side from shop metafield goldzenn.gold_prices
 *      (theme-independent; no network request, no 404 risk)
 *   2. window.GZ_GOLD_JSON_URL — correct asset_url of gold-prices-live.json on the CURRENT
 *      theme (fallback fetch, only if inline data missing or older than 48h)
 * Populates .mak-gold-price / .mak-gold-spot / .mak-gold-silver / .mak-gold-timestamp
 * elements and exposes window.GoldPriceFeed for calculators.
 * Graceful degradation: on total failure the static fallback values in the HTML remain
 * (those statics are refreshed nightly by the on-box updater, so they are never stale).
 */
(function () {
  'use strict';

  var CACHE_KEY = 'gz_gold_prices_v2';
  var CACHE_TTL = 22 * 60 * 1000;        // 22 minutes
  var STALE_MS = 48 * 60 * 60 * 1000;    // treat data older than 48h as stale

  function isValid(data) {
    return !!(data && data.karats && data.spot_oz && data.updated);
  }

  function ageMs(data) {
    var t = Date.parse(data.updated);
    return isNaN(t) ? Infinity : (Date.now() - t);
  }

  function getCached() {
    try {
      var raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var cached = JSON.parse(raw);
      if (Date.now() - cached._ts > CACHE_TTL) {
        localStorage.removeItem(CACHE_KEY);
        return null;
      }
      return isValid(cached.data) ? cached.data : null;
    } catch (e) {
      return null;
    }
  }

  function setCache(data) {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({ _ts: Date.now(), data: data }));
    } catch (e) { /* quota exceeded — silent fail */ }
  }

  function fmt(n) {
    return '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  // Half-up rounding written as floor(x * f + 0.5) / f so that it is the exact
  // IEEE-754 twin of half_up() in update_gold_prices.py. Do not "simplify" this
  // to toFixed() or Math.round() alone — the point is that both renderers land
  // on the same string for a value like 50% of melt.
  function halfUp(x, nd) {
    var f = Math.pow(10, nd);
    var s = x < 0 ? -1 : 1;
    return s * Math.floor(Math.abs(x) * f + 0.5) / f;
  }

  function fmtCurrencyD(n) {
    return '$' + halfUp(Number(n), 2).toLocaleString('en-US',
      { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  function fmtIntD(n) {
    return '$' + halfUp(Number(n), 0).toLocaleString('en-US', { maximumFractionDigits: 0 });
  }

  // Month names are localized off the document language so /es/ pages do not show
  // English abbreviations. Spanish convention is "10 ago 2026" (day first, no comma).
  var MONTHS = {
    en: ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'],
    es: ['ene','feb','mar','abr','may','jun','jul','ago','sep','oct','nov','dic']
  };

  function fmtDate(iso) {
    var d = new Date(iso);
    var lang = (document.documentElement.getAttribute('lang') || 'en').slice(0, 2).toLowerCase();
    var months = MONTHS[lang] || MONTHS.en;
    if (lang === 'es') {
      return d.getUTCDate() + ' ' + months[d.getUTCMonth()] + ' ' + d.getUTCFullYear();
    }
    return months[d.getUTCMonth()] + ' ' + d.getUTCDate() + ', ' + d.getUTCFullYear();
  }

  function populate(data) {
    if (!isValid(data)) return;

    var els = document.querySelectorAll('.mak-gold-price');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      var karat = el.getAttribute('data-karat');
      var unit = el.getAttribute('data-unit') || 'gram';
      if (karat && data.karats && data.karats[karat]) {
        var val = data.karats[karat][unit];
        if (val !== undefined) el.textContent = fmt(val);
      }
    }

    var derived = document.querySelectorAll('.mak-gold-derived');
    for (var n = 0; n < derived.length; n++) {
      var de = derived[n];
      var dk = de.getAttribute('data-karat');
      if (!dk || !data.karats || !data.karats[dk]) continue;
      var du = de.getAttribute('data-unit') || 'gram';
      var base = data.karats[dk][du];
      if (base === undefined || base === null) continue;
      var mult = parseFloat(de.getAttribute('data-mult'));
      if (isNaN(mult)) mult = 1;
      var dv = Number(base) * mult;
      de.textContent = (de.getAttribute('data-fmt') === 'int') ? fmtIntD(dv) : fmtCurrencyD(dv);
    }

    var spots = document.querySelectorAll('.mak-gold-spot');
    for (var j = 0; j < spots.length; j++) {
      spots[j].textContent = fmt(data.spot_oz);
    }

    var silvers = document.querySelectorAll('.mak-gold-silver');
    for (var k = 0; k < silvers.length; k++) {
      silvers[k].textContent = fmt(data.silver_oz);
    }

    var stamps = document.querySelectorAll('.mak-gold-timestamp');
    for (var m = 0; m < stamps.length; m++) {
      stamps[m].textContent = fmtDate(data.updated);
    }

    // Auto-detect existing Gold Prices article calculator
    var calcSpot = document.getElementById('calc-spot');
    if (calcSpot) {
      calcSpot.value = data.spot_oz;
      var evt = new Event('input', { bubbles: true });
      calcSpot.dispatchEvent(evt);
      if (typeof window.updateKaratTable === 'function') {
        window.updateKaratTable();
      }
    }
  }

  function fetchJson(url, callback) {
    var xhr = new XMLHttpRequest();
    xhr.open('GET', url, true);
    xhr.timeout = 8000;
    xhr.onload = function () {
      if (xhr.status === 200) {
        try {
          var data = JSON.parse(xhr.responseText);
          callback(isValid(data) ? data : null);
        } catch (e) { callback(null); }
      } else {
        callback(null);
      }
    };
    xhr.onerror = function () { callback(null); };
    xhr.ontimeout = function () { callback(null); };
    xhr.send();
  }

  function resolveData(callback) {
    var inline = isValid(window.GZ_GOLD_DATA) ? window.GZ_GOLD_DATA : null;

    // Fresh inline data: use it directly, zero network requests.
    if (inline && ageMs(inline) <= STALE_MS) {
      callback(inline);
      return;
    }

    var cached = getCached();
    if (cached && ageMs(cached) <= STALE_MS) {
      callback(cached);
      return;
    }

    // Inline missing or stale: try the theme asset as fallback.
    if (window.GZ_GOLD_JSON_URL) {
      fetchJson(window.GZ_GOLD_JSON_URL, function (fetched) {
        if (fetched && (!inline || ageMs(fetched) < ageMs(inline))) {
          setCache(fetched);
          callback(fetched);
        } else {
          callback(inline); // may be stale but is dated on-page; better than nothing
        }
      });
    } else {
      callback(inline);
    }
  }

  function init() {
    resolveData(function (data) {
      if (isValid(data)) {
        populate(data);

        window.GoldPriceFeed = {
          data: data,
          getSpot: function () { return data.spot_oz; },
          getSilver: function () { return data.silver_oz; },
          getKarat: function (k, unit) {
            unit = unit || 'gram';
            return (data.karats && data.karats[k]) ? data.karats[k][unit] : null;
          },
          // spot / 31.1035 — 999.9 fine gold per gram. Article calculators must
          // use this and NOT getKarat('24') for "spot price per gram": 24K is
          // x0.999, so the two differ by ~$0.14/g and the difference shows up on
          // the page as a division that does not check out.
          getPure: function (unit) {
            unit = unit || 'gram';
            if (data.karats && data.karats.pure) return data.karats.pure[unit];
            if (unit === 'gram' && data.pergram_pure) return data.pergram_pure;
            return null;
          },
          getDerived: function (k, unit, mult) {
            var base = this.getKarat(k, unit);
            return (base === null || base === undefined) ? null : Number(base) * (mult || 1);
          },
          getUpdated: function () { return data.updated; },
          refresh: function (cb) {
            localStorage.removeItem(CACHE_KEY);
            resolveData(function (fresh) {
              if (isValid(fresh)) {
                populate(fresh);
                window.GoldPriceFeed.data = fresh;
              }
              if (typeof cb === 'function') cb(fresh);
            });
          }
        };

        document.dispatchEvent(new CustomEvent('goldPriceFeedReady', { detail: data }));
      }
      // If data is null, static fallback values in HTML remain visible
      // (kept fresh nightly by the on-box updater — never silently stale).
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
